import { useEffect, useState, useMemo, useCallback } from 'react'
import style from './Feed.module.css'
import FeedCard from './FeedCard'
import FeedFilters from './FeedFilters'
import FilterIcon from '../../../assets/svgs/Filter'
import useDroppable from '../../../hooks/useDroppable'
import {
  transformCourses,
  transformEvents,
  transformJobs,
  transformNews,
} from '../../../utils/FeedTransformData'

// Simple Search Icon Component
const SearchIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={style.searchIcon}
  >
    <path
      d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z"
      stroke="#6b7280"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M21 21L16.65 16.65"
      stroke="#6b7280"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const API_URL = process.env.REACT_APP_API_URL || ''

const EMPTY_FILTERS = {
  domain_keys: [],
  country_codes: [],
  job_types: [],
  required_languages: [],
  publishers: [],
  providers: [],
}

const Feed = () => {
  const filters = ['All', 'Jobs', 'Courses', 'Events', 'News']

  const [selectedFilters, setSelectedFilters] = useState(['All'])
  const [feed, setFeed] = useState([])
  const [openFilter, setOpenFilter] = useState(false)

  // Search & Sort & Pagination
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const sortBy = 'date_desc'
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)

  // Advanced filters state (arrays for multi-select)
  const [advancedFilters, setAdvancedFilters] = useState({ ...EMPTY_FILTERS })

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
      if (searchTerm !== debouncedSearchTerm) {
        setCurrentPage(1)
      }
    }, 400)
    return () => clearTimeout(handler)
  }, [searchTerm, debouncedSearchTerm])

  // Reset page when advanced filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [advancedFilters])

  const droppableProps = useDroppable(feed, setFeed)

  const handleFiltersChange = useCallback((newFilters) => {
    setAdvancedFilters(newFilters)
  }, [])

  // Active category for showing/hiding filter sections
  const activeCategory = useMemo(() => {
    if (selectedFilters.includes('All')) return 'All'
    if (selectedFilters.length === 1) return selectedFilters[0]
    return 'All'
  }, [selectedFilters])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const query = debouncedSearchTerm ? `&search=${encodeURIComponent(debouncedSearchTerm)}` : ''

        // Build comma-separated filter query params
        const af = advancedFilters
        const commonParams = []
        if (af.domain_keys.length)  commonParams.push(`domain_keys=${af.domain_keys.join(',')}`)
        if (af.country_codes.length) commonParams.push(`country_codes=${af.country_codes.join(',')}`)
        const commonQ = commonParams.length ? '&' + commonParams.join('&') : ''

        // Jobs-specific
        const jobExtra = []
        if (af.job_types.length)          jobExtra.push(`job_types=${af.job_types.join(',')}`)
        if (af.required_languages.length) jobExtra.push(`required_languages=${af.required_languages.join(',')}`)
        const jobQ = jobExtra.length ? '&' + jobExtra.join('&') : ''

        // Courses-specific
        const courseQ = af.publishers.length ? `&publishers=${af.publishers.join(',')}` : ''

        // News-specific
        const newsQ = af.providers.length ? `&providers=${af.providers.join(',')}` : ''

        const [jobsData, coursesData, eventsData, newsData] = await Promise.all([
          fetch(`${API_URL}/feed/jobs?page=${currentPage}&limit=10${query}${commonQ}${jobQ}`).then((res) => res.json()),
          fetch(`${API_URL}/feed/courses?page=${currentPage}&limit=10${query}${commonQ}${courseQ}`).then((res) => res.json()),
          fetch(`${API_URL}/feed/events?page=${currentPage}&limit=10${query}${commonQ}`).then((res) => res.json()),
          fetch(`${API_URL}/feed/news?page=${currentPage}&limit=10${query}${commonQ}${newsQ}`).then((res) => res.json()),
        ])

        const transformedJobs = transformJobs(jobsData)
        const transformedCourses = transformCourses(coursesData)
        const transformedEvents = transformEvents(eventsData)
        const transformedNews = transformNews(newsData)

        const newFeed = [
          ...transformedJobs,
          ...transformedCourses,
          ...transformedEvents,
          ...transformedNews,
        ]

        setHasMore(newFeed.length > 0)
        setFeed(newFeed)
      } catch (error) {
        console.error('Error fetching or transforming data:', error)
      }
    }

    fetchData()
  }, [currentPage, debouncedSearchTerm, advancedFilters])

  const handleSelect = (filter) => {
    if (filter === 'All') {
      setSelectedFilters(['All'])
    } else {
      let newFilters = selectedFilters.includes('All')
        ? []
        : [...selectedFilters]

      if (newFilters.includes(filter)) {
        newFilters = newFilters.filter((f) => f !== filter)
      } else {
        newFilters.push(filter)
      }

      if (newFilters.length === 0) {
        setSelectedFilters(['All'])
      } else {
        setSelectedFilters(newFilters)
      }
    }
  }

  // --- Filtering Logic ---
  const filteredFeed = useMemo(() => {
    return feed.filter((item) => {
      const categoryMatch =
        selectedFilters.includes('All') || selectedFilters.includes(item.category)
      return categoryMatch
    })
  }, [feed, selectedFilters])

  // --- Sorting Logic ---
  const sortedFeed = useMemo(() => {
    const data = [...filteredFeed]

    data.sort((a, b) => {
      switch (sortBy) {
        case 'az':
          return (a.title || '').localeCompare(b.title || '')
        case 'za':
          return (b.title || '').localeCompare(a.title || '')
        case 'date_asc':
          return new Date(a.datePosted) - new Date(b.datePosted)
        case 'date_desc':
        default:
          return new Date(b.datePosted) - new Date(a.datePosted)
      }
    })
    return data
  }, [filteredFeed, sortBy])

  // --- Grouping Logic Removed ---

  const renderCardList = (items) => {
    return items.map((feedItem) => {
      const originalIndex = feed.findIndex(i => i.id === feedItem.id);
      const safeIndex = originalIndex >= 0 ? originalIndex : 0;

      return (
        <div
          key={feedItem.id || Math.random()}
          onDrop={(e) => droppableProps.onDrop(e, safeIndex)}
          onDragOver={droppableProps.onDragOver}
        >
          <FeedCard feedItem={feedItem} index={safeIndex} />
        </div>
      )
    })
  }

  return (
    <div className={style.container}>

      <div className={style.layoutWrapper}>
        {/* Left Sidebar: Filters */}
        <div className={`${style.sidebarColumn} ${openFilter ? style.mobileSidebarOpen : ''}`}>
          {openFilter && (
            <button className={style.mobileCloseBtn} onClick={() => setOpenFilter(false)}>
              ✕ Close Filters
            </button>
          )}
          <FeedFilters
            activeCategory={activeCategory}
            filters={advancedFilters}
            onFiltersChange={handleFiltersChange}
          />
        </div>

        {/* Right Content: Search, Categories, Feed */}
        <div className={style.mainFeedColumn}>
          
          <div className={style.topControls}>
            <div className={style.searchRow}>
              <div className={style.searchWrapper}>
                <SearchIcon />
                <input
                  type="text"
                  className={style.searchInput}
                  placeholder="Search feeds..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div onClick={() => setOpenFilter(true)} className={style.mobileFilterBtn}>
                <FilterIcon />
                {selectedFilters.length > 0 && !selectedFilters.includes('All') && (
                  <div className={style.filterBubble}>{selectedFilters.length}</div>
                )}
              </div>
            </div>

            <div className={style.filterContainer}>
              {filters.map((filter, index) => (
                <div
                  className={`${style.filter} ${selectedFilters.includes(filter) ? style.selected : ''}`}
                  key={index}
                  onClick={() => handleSelect(filter)}
                >
                  {filter}
                </div>
              ))}
            </div>
          </div>



      {/* Feed Content */}
      <div className={style.cardsContainer}>
        {sortedFeed.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
            No items found.
          </div>
        ) : (
          <div className={style.filteredFeed}>
            {renderCardList(sortedFeed)}
          </div>
        )}
      </div>

      <div className={style.paginationContainer}>
        <button 
          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
          disabled={currentPage === 1}
          className={style.paginationBtn}
          style={{ opacity: currentPage === 1 ? 0.5 : 1, pointerEvents: currentPage === 1 ? 'none' : 'auto' }}
        >
          Previous
        </button>
        <span className={style.paginationText}>
          Page {currentPage}
        </span>
        <button 
          onClick={() => setCurrentPage(p => p + 1)}
          disabled={!hasMore || sortedFeed.length === 0}
          className={style.paginationBtn}
          style={{ opacity: (!hasMore || sortedFeed.length === 0) ? 0.5 : 1, pointerEvents: (!hasMore || sortedFeed.length === 0) ? 'none' : 'auto' }}
        >
          Next
        </button>
      </div>

        </div> {/* End mainFeedColumn */}
      </div> {/* End layoutWrapper */}
    </div>
  )
}


export default Feed
